// d3-sankey visualisation for the PJ weekly-solution CPLEX output.
//
// Data shape comes from cplex_models/pj_weekly_solution/sankey_builder.py:
//   { models: [...], stages: [...], by_model: { <model>: { nodes, links, totals } } }
//
// Features per design review (review_id w6gs8):
//   - Model selector dropdown (no_pos | 5_day | full)
//   - Fullscreen toggle via the Fullscreen API
//   - Pan + wheel-zoom via d3.zoom on a <g> wrapper
//   - Click a node or a link to highlight every flow that touches it;
//     other elements fade. Click empty space to clear.
//
// Heavy data prep (link aggregation, totals) is done by the VM and shipped
// in run.result_tables.sankey — we only do layout + render here.

import { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import {
  sankey as d3Sankey,
  sankeyLinkHorizontal,
  type SankeyGraph,
} from 'd3-sankey';

import styles from './SankeyChart.module.css';
import { bakeLayout, type LayoutSnapshot, wrapLabel } from './sankeyLayout';

// ---------------------------------------------------------------------------
// Payload types (mirror the Python builder)
// ---------------------------------------------------------------------------

export interface SankeyNode {
  id: string;
  stage: string;
  label: string;
}

export interface SankeyLink {
  source: string;
  target: string;
  value: number;
  lbs: number;
  units: number;
  rows: number;
}

export interface SankeyModelPayload {
  nodes: SankeyNode[];
  links: SankeyLink[];
  totals: {
    lbs: number;
    orders: number;
    links: number;
    nodes: number;
  };
}

export interface SankeyPayload {
  models: string[];
  stages: string[];
  by_model: Record<string, SankeyModelPayload>;
  generated_at?: string;
  error?: string;
}

// External selection target — node id or link key. Re-uses the dailyView
// SelectedTarget shape so the chart and the dashboard share one contract.
import type { SelectedTarget as SankeyChartSelection } from './dailyView/types';

interface SankeyChartProps {
  payload: SankeyPayload;
  /** Optional override for which model the chart renders. When omitted the chart
   * defaults to the first model in the payload and exposes its own selector. */
  activeModel?: string;
  /** When set, the chart hides its own model selector + totals strip + inline
   * detail panel — the parent is owning the chrome. */
  embedded?: boolean;
  /** Controlled selection. When provided, the chart calls `onSelect` instead
   * of using its internal selectedNode/selectedLink state. */
  selected?: SankeyChartSelection | null;
  onSelect?: (s: SankeyChartSelection | null) => void;
  /**
   * Target on-screen font size (in CSS pixels) for inside-bar labels.
   * Defaults to 11.
   */
  labelFontSize?: number;
  /**
   * When provided, nodes NOT in this set are dimmed (and links whose endpoints
   * are both outside the set are dimmed). Composes with the click-selection
   * focus mode: when both are active, a node/link must be in BOTH sets to
   * stay opaque. Pass `null` (or omit) to disable filter highlighting.
   */
  filterHighlightNodeIds?: ReadonlySet<string> | null;
}

// d3-sankey enriches the input objects in-place with x0/x1/y0/y1; carry
// those positions through TypeScript without losing our original fields.
type LaidOutNode = SankeyNode & {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  index: number;
};
type LaidOutLink = Omit<SankeyLink, 'source' | 'target'> & {
  source: LaidOutNode;
  target: LaidOutNode;
  width: number;
  y0: number;
  y1: number;
  index: number;
};

// Color one column per stage so the eye can follow the supply chain.
const STAGE_COLORS: Record<string, string> = {
  filled_from: '#2563eb',
  po_vendor: '#f59e0b',
  raws: '#d97706',
  bulk: '#a16207',
  container: '#0891b2',
  'rm type': '#7c3aed',
  sku: '#16a34a',
  order: '#dc2626',
};

const formatLbs = d3.format(',.0f');
const formatInt = d3.format(',');

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SankeyChart({
  payload,
  activeModel: activeModelProp,
  embedded = false,
  selected,
  onSelect,
  labelFontSize = 11,
  filterHighlightNodeIds = null,
}: SankeyChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const zoomGroupRef = useRef<SVGGElement | null>(null);

  const [internalModel, setInternalModel] = useState<string>(
    payload.models[0] ?? '',
  );
  const activeModel = activeModelProp ?? internalModel;
  // Controlled vs uncontrolled selection — when `selected`/`onSelect` are passed,
  // the chart is fully controlled and never reads from internal state.
  const isControlled = onSelect != null;
  const [internalSelected, setInternalSelected] = useState<SankeyChartSelection | null>(null);
  const effectiveSelected = isControlled ? selected ?? null : internalSelected;
  const selectedNode = effectiveSelected?.kind === 'node' ? effectiveSelected.nodeId : null;
  // Internal link selection is index-based; controlled is key-based. The chart
  // converts between the two when needed using the laid-out link list.
  const [size, setSize] = useState({ width: 1100, height: 620 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Live zoom factor (d3.zoom transform.k). Drives per-node label
  // visibility: a label only renders when the bar at the current zoom is
  // tall enough to hold it. Zooming in past the per-bar threshold reveals
  // more labels; zooming out hides them so nothing overlaps.
  const [zoomK, setZoomK] = useState(1);

  // Re-measure when the container resizes (e.g. after entering fullscreen).
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      setSize({
        width: Math.max(800, Math.floor(rect.width)),
        height: Math.max(500, Math.floor(rect.height - 60)),
      });
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const handler = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const modelPayload = payload.by_model[activeModel];
  const stages = payload.stages;

  // d3-sankey mutates its input — give it fresh copies on every render
  // so React reconciliation doesn't see stale x0/x1/y0/y1.
  const laidOut = useMemo(() => {
    if (!modelPayload || modelPayload.nodes.length === 0) return null;

    const nodes = modelPayload.nodes.map((n) => ({ ...n }));
    const links = modelPayload.links.map((l) => ({ ...l }));

    const stageIndex = new Map(stages.map((s, i) => [s, i]));
    const sankeyGen = d3Sankey<SankeyNode, SankeyLink>()
      .nodeId((n) => n.id)
      // Pin every node to its declared stage column so the chart reads
      // left-to-right (filled_from → … → order) regardless of topology.
      // Returning the stage index also bumps the column count up to len(stages).
      .nodeAlign((n) => stageIndex.get((n as SankeyNode).stage) ?? 0)
      // Wide bars so labels can live INSIDE each rectangle rather than
      // outside it. Minimal vertical padding keeps columns continuous.
      .nodeWidth(140)
      .nodePadding(1)
      .extent([
        [80, 40],
        [size.width - 180, size.height - 40],
      ]);

    // d3-sankey throws on degenerate graphs (orphan nodes, cycles). A throw in
    // this memo would white-screen the whole page, so fail soft: return null and
    // let the component render its "no Sankey data" fallback instead.
    try {
      const graph: SankeyGraph<SankeyNode, SankeyLink> = sankeyGen({
        nodes,
        links,
      });
      return graph as {
        nodes: LaidOutNode[];
        links: LaidOutLink[];
      };
    } catch (err) {
      console.warn('SankeyChart: d3-sankey layout failed; rendering fallback', err);
      return null;
    }
  }, [modelPayload, stages, size.width, size.height]);

  // Pan-Y + zoom-K via d3.zoom. We DON'T apply the transform to the inner <g>
  // for scaling — the bake-layout function consumes zoomK directly and produces
  // baked positions. This means text glyphs render at their natural font size
  // (no SVG vertical stretching). The y-component of the transform IS applied
  // as a translate-only pan when no selection is active.
  const [panY, setPanY] = useState(0);
  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, 12])
      .on('zoom', (event) => {
        setZoomK(event.transform.k);
        setPanY(event.transform.y);
      });
    svg.call(zoom);
    svg.on('dblclick.zoom', null);
    return () => {
      svg.on('.zoom', null);
    };
  }, [laidOut]);

  // Resolve the selected link to its index in the laid-out list, whether the
  // chart is controlled (key-based) or uncontrolled (index-based).
  const selectedLink = useMemo<number | null>(() => {
    if (!laidOut) return null;
    if (effectiveSelected?.kind !== 'link') return null;
    const idx = laidOut.links.findIndex(
      (l) => `${l.source.id}=>${l.target.id}` === effectiveSelected.linkKey,
    );
    return idx === -1 ? null : idx;
  }, [laidOut, effectiveSelected]);

  // Which links should stay opaque under the current click-selection? (Filter
  // chips contribute via `effectiveNodeHighlight` below; this set is just
  // the click-traversal chain.)
  const highlightedLinkIdx = useMemo(() => {
    if (!laidOut) return null;
    if (selectedLink != null) return new Set<number>([selectedLink]);
    if (selectedNode == null) return null;
    const target = new Set<number>();
    const visit = (
      nodeId: string,
      direction: 'down' | 'up',
      seen: Set<string>,
    ) => {
      if (seen.has(nodeId)) return;
      seen.add(nodeId);
      laidOut.links.forEach((link, idx) => {
        if (direction === 'down' && link.source.id === nodeId) {
          target.add(idx);
          visit(link.target.id, 'down', seen);
        } else if (direction === 'up' && link.target.id === nodeId) {
          target.add(idx);
          visit(link.source.id, 'up', seen);
        }
      });
    };
    visit(selectedNode, 'down', new Set());
    visit(selectedNode, 'up', new Set());
    return target;
  }, [laidOut, selectedNode, selectedLink]);

  // Precomputed set of node ids that are endpoints of any highlighted link,
  // plus the selected node itself. This drives node-dim — using a string
  // Set decouples it from any index alignment between laidOut.links and
  // bakedLayout.links (which could drift if bakeLayout filters a link).
  const clickHighlightedNodeIds = useMemo(() => {
    if (!laidOut || highlightedLinkIdx == null) return null;
    const ids = new Set<string>();
    if (selectedNode) ids.add(selectedNode);
    highlightedLinkIdx.forEach((idx) => {
      const link = laidOut.links[idx];
      if (link) {
        ids.add(link.source.id);
        ids.add(link.target.id);
      }
    });
    return ids;
  }, [laidOut, highlightedLinkIdx, selectedNode]);

  // Compose the click-selection highlight with the external filter highlight.
  // Each may be null (= "match all"). When both are present, a node must be
  // in both sets to stay opaque (strict intersection).
  const effectiveNodeHighlight = useMemo<ReadonlySet<string> | null>(() => {
    if (!filterHighlightNodeIds && !clickHighlightedNodeIds) return null;
    if (!filterHighlightNodeIds) return clickHighlightedNodeIds;
    if (!clickHighlightedNodeIds) return filterHighlightNodeIds;
    const intersect = new Set<string>();
    for (const id of clickHighlightedNodeIds) {
      if (filterHighlightNodeIds.has(id)) intersect.add(id);
    }
    return intersect;
  }, [filterHighlightNodeIds, clickHighlightedNodeIds]);

  // Bake live positions from the natural d3-sankey layout + current zoom +
  // composed highlight set. Passing `forcedHighlight` to bakeLayout makes
  // search-filter chips trigger the same focus-mode reflow (grow focused
  // chain, squeeze unfocused, enforce min-height per focused node) that a
  // direct click does — otherwise small nodes pulled in by a chip would
  // stay too tiny to read.
  const bakedLayout = useMemo(() => {
    if (!laidOut) return null;
    const snapshot: LayoutSnapshot = {
      nodes: laidOut.nodes.map((n) => ({
        id: n.id,
        stage: n.stage,
        label: n.label,
        x0: n.x0,
        x1: n.x1,
        y0: n.y0,
        y1: n.y1,
        hOrig: n.y1 - n.y0,
      })),
      links: laidOut.links.map((l) => ({
        sourceId: l.source.id,
        targetId: l.target.id,
        value: l.value,
        lbs: l.lbs,
        units: l.units,
        rows: l.rows,
      })),
    };
    return bakeLayout({
      snapshot,
      stages,
      height: size.height,
      zoomK,
      selected: effectiveSelected,
      forcedHighlight: effectiveNodeHighlight,
      // One line of stroked text needs more than `lineHeight` to render
      // cleanly: paint-order=stroke adds labelFontSize*0.25 of outline on
      // every glyph edge, and the text baseline sits below the node centre
      // by ~0.35em. 1.3× barely covers the line itself and clips the stroke,
      // which is what the user saw when chain-bumped nodes still felt
      // unreadable. 1.9× leaves room for the stroke + a small breathing gap.
      minFocusedNodeH: labelFontSize * 1.9,
    });
  }, [
    laidOut,
    stages,
    size.height,
    zoomK,
    effectiveSelected,
    effectiveNodeHighlight,
    labelFontSize,
  ]);

  // A link is "active" when (a) its endpoints are both in the effective
  // node-highlight set, AND (b) when click-focus is in play, the link itself
  // is in the click chain (so unrelated links inside the filter set don't
  // pop back open when a node is selected).
  const effectiveLinkHighlight = useMemo<ReadonlySet<number> | null>(() => {
    if (!laidOut) return null;
    if (!effectiveNodeHighlight && highlightedLinkIdx == null) return null;
    const out = new Set<number>();
    laidOut.links.forEach((link, idx) => {
      if (
        effectiveNodeHighlight &&
        !(
          effectiveNodeHighlight.has(link.source.id) &&
          effectiveNodeHighlight.has(link.target.id)
        )
      ) {
        return;
      }
      if (highlightedLinkIdx && !highlightedLinkIdx.has(idx)) return;
      out.add(idx);
    });
    return out;
  }, [laidOut, effectiveNodeHighlight, highlightedLinkIdx]);

  // Suppress unused-import lint for sankeyLinkHorizontal — we now draw paths
  // from the baked layout's y0/y1 endpoints via a local renderLinkPath helper
  // (see bottom of file). The d3-sankey import is still needed for the
  // initial layout pass via d3Sankey() above.
  void sankeyLinkHorizontal;

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;
    if (document.fullscreenElement === containerRef.current) {
      await document.exitFullscreen();
    } else {
      await containerRef.current.requestFullscreen();
    }
  };

  const clearSelection = () => {
    if (isControlled) onSelect?.(null);
    else setInternalSelected(null);
  };
  const selectNode = (id: string) => {
    if (isControlled) onSelect?.({ kind: 'node', nodeId: id });
    else setInternalSelected({ kind: 'node', nodeId: id });
  };
  const selectLink = (key: string) => {
    if (isControlled) onSelect?.({ kind: 'link', linkKey: key });
    else setInternalSelected({ kind: 'link', linkKey: key });
  };

  if (payload.error) {
    return (
      <div className={styles.error}>
        <strong>Sankey generation failed:</strong> {payload.error}
      </div>
    );
  }
  if (!modelPayload) {
    return (
      <div className={styles.error}>
        No Sankey data available for model <code>{activeModel}</code>.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${
        isFullscreen ? styles.fullscreen : ''
      }`}
    >
      {!embedded && (
        <div className={styles.toolbar}>
          <label className={styles.modelSelector}>
            Model:
            <select
              value={activeModel}
              onChange={(e) => {
                setInternalModel(e.target.value);
                clearSelection();
              }}
            >
              {payload.models.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>

          <div className={styles.totals}>
            <span>
              <strong>{formatLbs(modelPayload.totals.lbs)}</strong> lbs
            </span>
            <span>
              <strong>{formatInt(modelPayload.totals.orders)}</strong> orders
            </span>
            <span>
              <strong>{formatInt(modelPayload.totals.links)}</strong> flows
            </span>
          </div>

          <div className={styles.actions}>
            {(selectedNode || selectedLink != null) && (
              <button onClick={clearSelection} className={styles.clearBtn}>
                Clear selection
              </button>
            )}
            <button onClick={toggleFullscreen} className={styles.fsBtn}>
              {isFullscreen ? 'Exit fullscreen' : 'Fullscreen ⛶'}
            </button>
          </div>
        </div>
      )}

      <svg
        ref={svgRef}
        width={size.width}
        height={size.height}
        className={styles.svg}
        onClick={(e) => {
          if (e.target === svgRef.current) clearSelection();
        }}
      >
        {/* No scale transform on the parent g — positions are baked into
            x0/y0/x1/y1 in bakedLayout. We only apply a Y-only translate for
            pan when there's no active selection (in focus mode the layout
            fills the viewport so pan would just push it off-screen). */}
        <g
          ref={zoomGroupRef}
          transform={effectiveSelected ? undefined : `translate(0, ${panY})`}
        >
          {/* Stage column labels along the top */}
          {bakedLayout &&
            stages.map((stage) => {
              const cols = bakedLayout.nodes.filter((n) => n.stage === stage);
              if (cols.length === 0) return null;
              const x = (cols[0].x0 + cols[0].x1) / 2;
              return (
                <text
                  key={stage}
                  x={x}
                  y={24}
                  textAnchor="middle"
                  className={styles.stageLabel}
                  fill={STAGE_COLORS[stage] ?? '#64748b'}
                >
                  {stage}
                </text>
              );
            })}

          {/* Links — drawn from baked positions with a horizontal bezier */}
          {bakedLayout?.links.map((link, idx) => {
            const dimmed =
              effectiveLinkHighlight != null && !effectiveLinkHighlight.has(idx);
            const stroke = STAGE_COLORS[link.source.stage] ?? '#94a3b8';
            return (
              <path
                key={idx}
                d={renderLinkPath(link)}
                stroke={stroke}
                strokeOpacity={dimmed ? 0.05 : 0.35}
                strokeWidth={Math.max(1, link.width)}
                fill="none"
                className={styles.link}
                onClick={(e) => {
                  e.stopPropagation();
                  selectLink(link.linkKey);
                }}
              >
                <title>
                  {link.source.label} → {link.target.label}
                  {'\n'}lbs: {formatLbs(link.lbs)} · units:{' '}
                  {formatInt(link.units)} · flows: {formatInt(link.rows)}
                </title>
              </path>
            );
          })}

          {/* Nodes — baked positions + natural-size multi-line labels */}
          {bakedLayout?.nodes.map((node) => {
            // A node is in the highlight set if it's selected or any
            // highlighted link touches it, AND it survives the external
            // filter set (when applied). Driven by `effectiveNodeHighlight`
            // which composes click-focus and search-filter sets.
            const dimmed =
              effectiveNodeHighlight != null &&
              !effectiveNodeHighlight.has(node.id);
            const color = STAGE_COLORS[node.stage] ?? '#64748b';
            const nodeWidth = node.x1 - node.x0;
            const nodeHeight = Math.max(1, node.y1 - node.y0);
            // Natural-size labels with multi-line wrap. Char-width estimate
            // assumes a 0.58 aspect (typical for the sans-serif at 11px).
            const charPx = labelFontSize * 0.58;
            const lineH = labelFontSize * 1.3;
            const availW = Math.max(0, nodeWidth - 8);
            const maxChars = Math.max(0, Math.floor(availW / charPx));
            const maxLines = Math.max(0, Math.floor(nodeHeight / lineH));
            const lines =
              maxLines === 0 || maxChars < 2
                ? []
                : wrapLabel(node.label, maxChars, maxLines);
            const cx = (node.x0 + node.x1) / 2;
            const cy = (node.y0 + node.y1) / 2;
            const blockTop = cy - ((lines.length - 1) * lineH) / 2;
            return (
              <g
                key={node.id}
                className={styles.node}
                opacity={dimmed ? 0.25 : 1}
                onClick={(e) => {
                  e.stopPropagation();
                  selectNode(node.id);
                }}
              >
                <rect
                  x={node.x0}
                  y={node.y0}
                  width={nodeWidth}
                  height={nodeHeight}
                  fill={color}
                  stroke="#0f172a"
                  strokeWidth={selectedNode === node.id ? 2 : 0}
                >
                  <title>
                    {node.stage}: {node.label}
                  </title>
                </rect>
                {lines.length > 0 && (
                  <text
                    x={cx}
                    y={blockTop}
                    textAnchor="middle"
                    className={styles.nodeLabel}
                    fontSize={labelFontSize}
                    fill="#ffffff"
                    style={{
                      paintOrder: 'stroke',
                      stroke: 'rgba(15, 23, 42, 0.55)',
                      strokeWidth: labelFontSize * 0.25,
                      pointerEvents: 'none',
                    }}
                  >
                    {lines.map((ln, i) => (
                      <tspan
                        key={i}
                        x={cx}
                        dy={i === 0 ? '0.35em' : `${lineH}px`}
                      >
                        {ln}
                      </tspan>
                    ))}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>

      {!embedded && (selectedNode || selectedLink != null) && (
        <div className={styles.detailPanel}>
          {selectedNode && (
            <SelectedNodeDetails
              nodeId={selectedNode}
              laidOut={laidOut}
            />
          )}
          {selectedLink != null && laidOut && (
            <SelectedLinkDetails link={laidOut.links[selectedLink]} />
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail panels
// ---------------------------------------------------------------------------

function SelectedNodeDetails({
  nodeId,
  laidOut,
}: {
  nodeId: string;
  laidOut: { nodes: LaidOutNode[]; links: LaidOutLink[] } | null;
}) {
  if (!laidOut) return null;
  const node = laidOut.nodes.find((n) => n.id === nodeId);
  if (!node) return null;
  const incoming = laidOut.links.filter((l) => l.target.id === nodeId);
  const outgoing = laidOut.links.filter((l) => l.source.id === nodeId);
  const lbsIn = incoming.reduce((a, l) => a + l.lbs, 0);
  const lbsOut = outgoing.reduce((a, l) => a + l.lbs, 0);

  return (
    <div>
      <div className={styles.detailHeader}>
        <span className={styles.stageTag}>{node.stage}</span>
        <strong>{node.label}</strong>
      </div>
      <div className={styles.detailRow}>
        <span>lbs in</span>
        <strong>{formatLbs(lbsIn)}</strong>
      </div>
      <div className={styles.detailRow}>
        <span>lbs out</span>
        <strong>{formatLbs(lbsOut)}</strong>
      </div>
      <div className={styles.detailRow}>
        <span>incoming flows</span>
        <strong>{formatInt(incoming.length)}</strong>
      </div>
      <div className={styles.detailRow}>
        <span>outgoing flows</span>
        <strong>{formatInt(outgoing.length)}</strong>
      </div>
    </div>
  );
}

// Bezier path for a baked link — emulates sankeyLinkHorizontal but reads the
// endpoint y values from our baked positions instead of d3-sankey's own.
function renderLinkPath(link: {
  source: { x1: number };
  target: { x0: number };
  y0: number;
  y1: number;
}): string {
  const x0 = link.source.x1;
  const x1 = link.target.x0;
  const xi = (x0 + x1) / 2;
  return `M${x0},${link.y0}C${xi},${link.y0} ${xi},${link.y1} ${x1},${link.y1}`;
}

function SelectedLinkDetails({ link }: { link: LaidOutLink }) {
  return (
    <div>
      <div className={styles.detailHeader}>
        <strong>{link.source.label}</strong>
        <span style={{ margin: '0 0.5rem' }}>→</span>
        <strong>{link.target.label}</strong>
      </div>
      <div className={styles.detailRow}>
        <span>lbs</span>
        <strong>{formatLbs(link.lbs)}</strong>
      </div>
      <div className={styles.detailRow}>
        <span>units</span>
        <strong>{formatInt(link.units)}</strong>
      </div>
      <div className={styles.detailRow}>
        <span>aggregated rows</span>
        <strong>{formatInt(link.rows)}</strong>
      </div>
    </div>
  );
}
